import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Send } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from './ui/Toast';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useComprobanteFormulario } from '../hooks/useComprobanteFormulario';
import { comprobanteService } from '../services/comprobanteService';
import { notificacionBalanceService } from '../services/notificacionBalanceService';
import { TipoEntidadBalance } from '../lib/supabase';
import type { ComprobanteFaltante, EntidadDinero } from '../types/comprobante';
import { Textarea } from './ui/Textarea';
import { DialogShell } from './ui/DialogShell';
import { ComprobanteInput } from './comprobantes/ComprobanteInput';
import { rutaPanelPendientes } from '../utils/comprobanteEntidad';
import { mensajeErrorDeNegocio } from '../utils/erroresDeNegocio';
import { formatCurrency, formatDate } from '../utils/formatters';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';
import { FormErrorAlert } from './ui/FormErrorAlert';

interface NotificarBalanceModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
  readonly entidadId: string;
  readonly tipoEntidad: TipoEntidadBalance;
  readonly mes: number;
  readonly año: number;
  readonly mode?: 'enviar' | 'reenviar';
  readonly notificacionId?: string;
}

const MESES_NOMBRES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

/** Lo que se revisó del mes antes de dejar enviarlo, para el mes y la entidad que lo pidieron. */
interface RevisionDelMes {
  readonly clave: string;
  readonly faltantes: readonly ComprobanteFaltante[];
  readonly cuotaPorTraspasar: number;
}

interface AvisosDelMesProps {
  readonly mesNombre: string;
  readonly revision: RevisionDelMes | null;
  readonly onIrAPendientes: () => void;
}

const AvisosDelMes: React.FC<AvisosDelMesProps> = ({ mesNombre, revision, onIrAPendientes }) => {
  if (!revision) return <p role="status" className="text-sm text-slate-500">Revisando el mes…</p>;
  const { faltantes, cuotaPorTraspasar } = revision;
  if (faltantes.length > 0) {
    return (
      <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 space-y-2">
        <p className="text-sm font-semibold text-red-800">
          No puedes enviar el balance de {mesNombre}: faltan comprobantes de {faltantes.length} {faltantes.length === 1 ? 'movimiento' : 'movimientos'}.
        </p>
        <ul className="text-sm text-red-700 space-y-0.5">
          {faltantes.map((f) => (
            <li key={`${f.movimiento_id}:${f.respaldo}`}>
              {formatDate(f.fecha)} · {f.descripcion ?? 'Sin descripción'} · {formatCurrency(Number(f.monto))}
            </li>
          ))}
        </ul>
        <button type="button" onClick={onIrAPendientes} className="min-h-11 text-sm font-semibold text-red-800 underline">
          Ir a los comprobantes pendientes
        </button>
      </div>
    );
  }
  if (cuotaPorTraspasar > 0) {
    return (
      <p className="text-sm text-slate-700">
        Cuota iglesia de {mesNombre} por transferir: <span className="font-semibold">{formatCurrency(cuotaPorTraspasar)}</span>
      </p>
    );
  }
  return <p className="text-sm text-slate-600">Cuota iglesia de {mesNombre}: $0. No hay transferencia que respaldar.</p>;
};

interface RevisionDelMesParams {
  readonly isOpen: boolean;
  readonly tipoEntidad: TipoEntidadBalance;
  readonly entidadId: string;
  readonly mes: number;
  readonly año: number;
}

/** Revisa el mes (comprobantes que faltan y cuota iglesia por transferir) mientras la ventana está abierta. */
function useRevisionDelMes({ isOpen, tipoEntidad, entidadId, mes, año }: RevisionDelMesParams) {
  const [revisionDelMes, setRevisionDelMes] = useState<RevisionDelMes | null>(null);

  // La revisión vale solo para el mes y la entidad que la pidieron; con otra clave se vuelve a revisar.
  const claveDelMes = isOpen && entidadId ? `${tipoEntidad}:${entidadId}:${año}-${mes}` : null;
  const revision = revisionDelMes?.clave === claveDelMes ? revisionDelMes : null;
  const cuotaPorTraspasar = revision?.cuotaPorTraspasar ?? null;
  const bloqueadoPorFaltantes = (revision?.faltantes.length ?? 0) > 0;
  const pideComprobante = revision !== null && !bloqueadoPorFaltantes && revision.cuotaPorTraspasar > 0;

  useEffect(() => {
    if (!claveDelMes) return;
    let vigente = true;
    const entidadDelMes: EntidadDinero = { tipo: tipoEntidad, id: entidadId, nombre: '' };
    Promise.all([
      comprobanteService.getFaltantesDelMes(entidadDelMes, mes, año),
      comprobanteService.getCuotaIglesiaPorTraspasar(tipoEntidad, entidadId, mes, año),
    ])
      .then(([faltantes, cuota]) => { if (vigente) setRevisionDelMes({ clave: claveDelMes, faltantes, cuotaPorTraspasar: cuota }); })
      .catch((e: unknown) => { if (vigente) reportarErrorInesperado(e, 'No pudimos revisar el mes antes de enviarlo.'); });
    return () => { vigente = false; };
  }, [claveDelMes, tipoEntidad, entidadId, mes, año]);

  const olvidarRevision = () => { setRevisionDelMes(null); };

  return { revision, cuotaPorTraspasar, bloqueadoPorFaltantes, pideComprobante, olvidarRevision };
}

interface EncabezadoDelEnvioProps {
  readonly mode: 'enviar' | 'reenviar';
  readonly mesNombre: string;
  readonly año: number;
  readonly onClose: () => void;
}

const EncabezadoDelEnvio: React.FC<EncabezadoDelEnvioProps> = ({ mode, mesNombre, año, onClose }) => {
  const reenvio = mode === 'reenviar';
  return (
    <div className="sticky top-0 z-10 bg-white border-b border-slate-200 p-6 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${reenvio ? 'bg-amber-100' : 'bg-blue-100'}`}>
          <Send className={`w-6 h-6 ${reenvio ? 'text-amber-600' : 'text-blue-600'}`} />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-800">
            {reenvio ? 'Reenviar Balance Corregido' : 'Notificar Envío de Balance'}
          </h2>
          <p className="text-sm text-slate-500">{mesNombre} {año}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
        aria-label="Cerrar modal"
      >
        <X className="w-6 h-6 text-slate-600" />
      </button>
    </div>
  );
};

interface AvisoDelEnvioProps {
  readonly mode: 'enviar' | 'reenviar';
  readonly tipoEntidad: TipoEntidadBalance;
  readonly mesNombre: string;
  readonly año: number;
}

const AvisoDelEnvio: React.FC<AvisoDelEnvioProps> = ({ mode, tipoEntidad, mesNombre, año }) => {
  const adminDestinatario = tipoEntidad === 'lumisial' ? 'administrador diocesano' : 'administrador nacional';
  if (mode === 'reenviar') {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-amber-800 text-sm">
          El balance de <span className="font-semibold">{mesNombre} {año}</span> fue observado.
          Al confirmar, se notificará al {adminDestinatario} que el balance ha sido corregido y está listo para una nueva revisión.
        </p>
      </div>
    );
  }
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <p className="text-blue-800 text-sm">
        Al confirmar, se enviará una notificación al {adminDestinatario} informando que el balance de{' '}
        <span className="font-semibold">{mesNombre} {año}</span> ha sido completado y está listo para revisión.
      </p>
    </div>
  );
};

export const NotificarBalanceModal: React.FC<NotificarBalanceModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  entidadId,
  tipoEntidad,
  mes,
  año,
  mode = 'enviar',
  notificacionId,
}) => {
  const { usuario } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const { error, clearError, handleError } = useErrorHandler();

  const [nota, setNota] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submitLabel = mode === 'reenviar' ? 'Reenviar Balance' : 'Enviar Notificación';
  const mesNombre = MESES_NOMBRES[mes - 1];
  const entidad: EntidadDinero = { tipo: tipoEntidad, id: entidadId, nombre: '' };
  const { revision, cuotaPorTraspasar, bloqueadoPorFaltantes, pideComprobante, olvidarRevision } =
    useRevisionDelMes({ isOpen, tipoEntidad, entidadId, mes, año });

  // El comprobante de la transferencia lo revisa el nivel superior al recibir: no se objeta ni se sube después.
  const comprobante = useComprobanteFormulario({
    entidad,
    fechaMovimiento: null,
    permiteSubirDespues: false,
    pideMedioPago: false,
    obligatoriedad: cuotaPorTraspasar !== null && cuotaPorTraspasar > 0 ? 'desde_vigencia' : 'nunca',
    mensajeFaltante: 'Adjunta el comprobante de la transferencia de la cuota iglesia de este mes.',
  });

  // Lo escrito, lo elegido y la revisión del mes se sueltan al cerrar, para que la próxima vez se empiece de cero.
  const resetAndClose = () => {
    setNota('');
    clearError();
    comprobante.reiniciar();
    olvidarRevision();
    onClose();
  };

  const irAPendientes = () => {
    resetAndClose();
    navigate(rutaPanelPendientes(entidad));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!usuario) {
      handleError('No se pudo identificar al usuario actual');
      return;
    }

    if (!entidadId) {
      handleError('No se ha seleccionado una entidad');
      return;
    }

    const errorComprobante = comprobante.validar();
    if (errorComprobante) {
      handleError(errorComprobante);
      return;
    }

    try {
      setSubmitting(true);
      const { comprobante_id } = await comprobante.resolver();

      if (mode === 'reenviar' && notificacionId) {
        // Sin comprobante nuevo se conserva el anterior.
        await notificacionBalanceService.reenviarNotificacion(
          notificacionId, usuario.user_id, nota.trim() || undefined, comprobante_id ?? undefined
        );
        toast.success('Balance corregido reenviado exitosamente');
      } else {
        await notificacionBalanceService.enviarNotificacion({
          tipo_entidad: tipoEntidad,
          entidad_id: entidadId,
          periodo_mes: mes,
          periodo_anio: año,
          nota: nota.trim() || undefined,
          enviado_por_usuario_id: usuario.user_id,
          comprobante_transferencia_id: comprobante_id,
        });
        toast.success('Notificación de balance enviada exitosamente');
      }
      onSuccess();
      resetAndClose();
    } catch (err: unknown) {
      const deNegocio = mensajeErrorDeNegocio(err);
      if (deNegocio) handleError(deNegocio);
      else reportarErrorInesperado(err, 'Error al enviar la notificación. Por favor intenta nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={resetAndClose}
      size="md"
      header={<EncabezadoDelEnvio mode={mode} mesNombre={mesNombre} año={año} onClose={resetAndClose} />}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <FormErrorAlert error={error} onClear={clearError} />

        <AvisoDelEnvio mode={mode} tipoEntidad={tipoEntidad} mesNombre={mesNombre} año={año} />
        <AvisosDelMes mesNombre={mesNombre} revision={revision} onIrAPendientes={irAPendientes} />

        {pideComprobante && (
          <ComprobanteInput
            comprobante={comprobante}
            titulo="Comprobante de la transferencia de cuota iglesia"
            deshabilitado={submitting}
          />
        )}

        <Textarea
          label="Nota adicional (opcional)"
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          placeholder="Agrega comentarios o aclaraciones sobre este balance..."
          rows={4}
        />

        <div className="flex gap-3 pt-4 border-t border-slate-200">
          <button
            type="button"
            onClick={resetAndClose}
            className="flex-1 px-6 py-3 bg-slate-100 text-slate-700 font-semibold rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="flex-1 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={submitting || revision === null || bloqueadoPorFaltantes || comprobante.subiendo}
          >
            {submitting ? 'Enviando...' : submitLabel}
          </button>
        </div>
      </form>
    </DialogShell>
  );
};
